import { getSubdomainBySubdomain, extractSubdomainFromHost } from './subdomainService';
import { getStaticPageURL, getStaticHTMLContent } from './staticHostingService';

/**
 * サブドメインのルーティング情報を取得します
 * @param {string} subdomain - サブドメイン
 * @param {string} path - パス（例: "/about", "/contact"）
 * @returns {Promise<Object>} ルーティング情報
 */
export const getSubdomainRoute = async (subdomain, path = '/') => {
    try {
        // サブドメイン情報を取得
        const subdomainInfo = await getSubdomainBySubdomain(subdomain);
        if (!subdomainInfo) {
            return {
                exists: false,
                error: 'サブドメインが見つかりません'
            };
        }

        // パスを正規化
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;

        // ルートパスの場合
        if (normalizedPath === '/' || normalizedPath === '') {
            console.log('🏠 サブドメインルートページの処理開始');

            // 1. 'index' slugでページを検索（新しいルートページ方式）
            try {
                const { getPublishedPageForSubdomain } = await import('./firestoreService');
                const indexPageData = await getPublishedPageForSubdomain('index', subdomainInfo.accountId);

                if (indexPageData) {
                    console.log('✅ インデックスページをFirestoreから取得:', indexPageData.title);
                    return {
                        exists: true,
                        type: 'firestore-page',
                        pageData: indexPageData,
                        slug: 'index',
                        subdomainInfo,
                        corsWorkaround: true,
                        isRootPage: true
                    };
                }
            } catch (firestoreError) {
                console.warn('❌ Firestoreインデックスページ取得失敗:', firestoreError);
            }

            // 2. 最終フォールバック: 従来のindex.html
            const indexUrl = await getStaticPageURL('index', subdomain);
            if (indexUrl) {
                console.log('📁 Storageからindex.htmlを取得');
                return {
                    exists: true,
                    type: 'index',
                    url: indexUrl,
                    subdomainInfo
                };
            }

            console.log('❌ サブドメインのルートページが見つかりません');
        }

        // 直接パスの場合（/about, /contact など）
        const directPath = normalizedPath.substring(1); // 先頭の "/" を除去

        // まずFirestoreから直接ページデータを取得を試行（CORS回避）
        try {
            const { getPublishedPageForSubdomain } = await import('./firestoreService');

            // サブドメイン所有者のアカウントIDでフィルタリング
            const pageData = await getPublishedPageForSubdomain(directPath, subdomainInfo.accountId);

            if (pageData) {
                console.log('✅ Firestoreからサブドメイン用ページデータを取得:', pageData.title);
                return {
                    exists: true,
                    type: 'firestore-page',
                    pageData: pageData,
                    slug: directPath,
                    subdomainInfo,
                    corsWorkaround: true // CORS回避フラグ
                };
            }

            // アカウント指定で見つからない場合は全体から検索
            const { getPublishedPageBySlug } = await import('./firestoreService');
            const fallbackPageData = await getPublishedPageBySlug(directPath);

            if (fallbackPageData) {
                console.log('✅ Firestore全体検索でページデータを取得:', fallbackPageData.title);
                return {
                    exists: true,
                    type: 'firestore-page',
                    pageData: fallbackPageData,
                    slug: directPath,
                    subdomainInfo,
                    corsWorkaround: true // CORS回避フラグ
                };
            }
        } catch (firestoreError) {
            console.warn('❌ Firestoreからのページ取得に失敗、Storageを試行:', firestoreError);

            // 権限エラーの詳細情報
            if (firestoreError.code === 'permission-denied') {
                console.warn('🚫 Firestore権限エラー詳細:');
                console.warn('- ページslug:', directPath);
                console.warn('- サブドメイン:', subdomain);
                console.warn('- 認証状態: 未認証（サブドメインアクセス）');
                console.warn('💡 解決案: Firestoreルールで公開ページ（status: published）の読み取りを許可してください');
            }
        }

        // フォールバック: Firebase Storageから取得
        const directPageUrl = await getStaticPageURL(directPath, subdomain);
        if (directPageUrl) {
            return {
                exists: true,
                type: 'page',
                url: directPageUrl,
                slug: directPath,
                subdomainInfo
            };
        }

        // 最終フォールバック: インデックスページを試行
        if (directPath !== '' && directPath !== 'index') {
            console.log('🏠 最終フォールバック: インデックスページを試行');
            try {
                const { getPublishedPageForSubdomain } = await import('./firestoreService');
                const indexPageData = await getPublishedPageForSubdomain('', subdomainInfo.accountId) ||
                    await getPublishedPageForSubdomain('index', subdomainInfo.accountId);

                if (indexPageData) {
                    console.log('✅ インデックスページをフォールバックとして使用:', indexPageData.title);
                    return {
                        exists: true,
                        type: 'firestore-page',
                        pageData: indexPageData,
                        slug: 'index',
                        subdomainInfo,
                        corsWorkaround: true,
                        fallbackToIndex: true
                    };
                }
            } catch (indexError) {
                console.warn('❌ インデックスページフォールバック失敗:', indexError);
            }
        }

        // デバッグ情報を追加
        console.log('🔍 ページが見つかりません:', {
            subdomain,
            path: normalizedPath,
            directPath,
            requestedUrl: directPageUrl,
            subdomainInfo
        });

        return {
            exists: false,
            error: `ページ「${directPath}」が見つかりません。このページを公開してください。`,
            subdomainInfo,
            debug: {
                subdomain,
                requestedPath: directPath,
                subdomainExists: !!subdomainInfo,
                firestoreChecked: true,
                storageChecked: true,
                indexFallbackChecked: true
            }
        };

    } catch (error) {
        console.error('サブドメインルーティングエラー:', error);
        return {
            exists: false,
            error: 'ルーティングエラーが発生しました'
        };
    }
};

/**
 * 現在のURLからサブドメインとパスを解析します
 * @returns {Object} サブドメインとパスの情報
 */
export const parseSubdomainURL = () => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    // サブドメインを抽出
    const subdomain = extractSubdomainFromHost(hostname);

    // デバッグ用ログ
    console.log('🔍 URL解析:', {
        hostname,
        pathname,
        subdomain,
        fullUrl: window.location.href
    });

    return {
        subdomain,
        path: pathname,
        fullUrl: window.location.href
    };
};

/**
 * サブドメイン用のHTMLファイルを生成する際のファイルパスを取得します
 * @param {string} subdomain - サブドメイン
 * @param {string} slug - ページスラッグ（オプション）
 * @returns {string} Firebase Storageのファイルパス
 */
export const getSubdomainFilePath = (subdomain, slug = null) => {
    if (slug) {
        return `subdomains/${subdomain}/pages/${slug}.html`;
    }
    return `subdomains/${subdomain}/index.html`;
};

/**
 * サブドメイン用のURLを生成します
 * @param {string} subdomain - サブドメイン
 * @param {string} slug - ページスラッグ（オプション）
 * @returns {string} 完全なURL
 */
export const generateSubdomainURL = (subdomain, slug = null) => {
    const baseUrl = `https://${subdomain}.suisui.life`;
    if (slug) {
        return `${baseUrl}/${slug}`;
    }
    return baseUrl;
}; 